import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { recordListingView, getRecentListing } from "@/lib/recentListings";
import { typeLabel, money, STATUS_META, listerLabel, isSharedType, genderPrefLabel } from "@/data/constants";
import { PaywallModal } from "@/components/PaywallModal";
import { MapView } from "@/components/MapView";
import { useFavorites } from "@/hooks/useFavorites";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  MapPin, Layers, Maximize, ArrowLeft, Lock, Phone, Mail, User,
  Building2, Map, PlayCircle, Loader2, Heart, ChevronLeft, ChevronRight, Clock, CalendarRange, Sparkles, Star, CalendarDays, ShieldCheck,
} from "lucide-react";

const FALLBACK =
  "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

const d2s = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const s2d = (s) => new Date(s + "T00:00:00");

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [contact, setContact] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [savingAvail, setSavingAvail] = useState(false);
  const [rating, setRating] = useState(0);
  const [rvName, setRvName] = useState("");
  const [rvComment, setRvComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { isFavorite, toggle } = useFavorites();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const unlocked = JSON.parse(localStorage.getItem("rento_unlocked") || "{}");
      const ref = unlocked[id];
      const { data } = await api.get(`/listings/${id}`, {
        params: ref ? { payment_ref: ref } : {},
      });
      setListing(data);
      recordListingView(data);
      if (data.contact) setContact(data.contact);
      setBlocked((data.blocked_dates || []).map(s2d));
      try {
        const { data: rv } = await api.get(`/listings/${id}/reviews`);
        setReviews(rv);
      } catch {
        /* noop */
      }
    } catch {
      // Offline fallback: show a previously viewed copy if we have one cached
      const cached = getRecentListing(id);
      setListing(cached || false);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      await api.put(`/listings/${id}/availability`, { blocked_dates: blocked.map(d2s) });
      toast.success("Availability saved");
    } catch {
      toast.error("Could not save availability");
    } finally {
      setSavingAvail(false);
    }
  };

  const submitReview = async () => {
    if (rating < 1) {
      toast.error("Please select a star rating");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/listings/${id}/reviews`, { rating, comment: rvComment, guest_name: rvName });
      setReviews((p) => [data.review, ...p]);
      setListing((l) => ({ ...l, rating_avg: data.rating_avg, rating_count: data.rating_count }));
      setRating(0);
      setRvName("");
      setRvComment("");
      toast.success("Thanks for your review!");
    } catch {
      toast.error("Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" />
      </div>
    );

  if (!listing)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <p className="font-display text-2xl text-[#2C3830]">Listing not found</p>
        <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#2C3830]">
          Back to browse
        </Button>
      </div>
    );

  const photos = listing.photos?.length ? listing.photos : [];
  const cover = photos.length ? fileUrl(photos[active]) : FALLBACK;
  const isOwner = user && (user.id === listing.owner_id || user.user_id === listing.owner_id || user.role === "admin");

  return (
    <div className="mx-auto max-w-7xl px-5 py-8 lg:px-8">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-1.5 text-sm text-[#6B7280] transition-colors hover:text-[#2C3830]"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid gap-8 lg:grid-cols-12">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border/60 bg-[#EDE9E1]">
            {showVideo && listing.video ? (
              <video
                data-testid="listing-video"
                src={fileUrl(listing.video)}
                controls
                autoPlay
                className="h-full w-full object-cover"
              />
            ) : (
              <img
                data-testid="listing-cover"
                src={cover}
                alt={listing.title}
                className="h-full w-full object-cover"
                onError={(e) => (e.currentTarget.src = FALLBACK)}
              />
            )}
            <div className="absolute left-4 top-4 flex gap-2">
              <Badge className="rounded-full border-0 bg-[#2C3830] capitalize text-white">
                {listing.listing_type === "short_term" ? "Short Stay" : `For ${listing.listing_type}`}
              </Badge>
              <Badge variant="outline" data-testid="lister-badge" className="rounded-full">
                {listing.listing_type === "short_term" ? "Host" : listerLabel(listing.lister_type)}
              </Badge>
              {listing.owner_verified ? (
                <Badge data-testid="detail-verified" className="rounded-full border-0 bg-[#1D4ED8] text-white"><ShieldCheck className="mr-1 h-3 w-3" /> Verified</Badge>
              ) : (
                <Badge data-testid="detail-unverified" className="rounded-full border-0 bg-[#9CA3AF] text-white">Unverified</Badge>
              )}
              {listing.status && listing.status !== "active" && (
                <Badge className="rounded-full border-0 text-white" style={{ backgroundColor: STATUS_META[listing.status].color }}>
                  {STATUS_META[listing.status].label}
                </Badge>
              )}
            </div>
            <button
              data-testid="detail-fav-toggle"
              onClick={() => toggle(listing.id)}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 backdrop-blur transition-transform hover:scale-110"
              title={isFavorite(listing.id) ? "Remove from saved" : "Save property"}
            >
              <Heart className={`h-5 w-5 ${isFavorite(listing.id) ? "fill-[#D4A373] text-[#D4A373]" : "text-[#2C3830]"}`} />
            </button>

            {photos.length > 1 && !showVideo && (
              <>
                <button
                  data-testid="gallery-prev"
                  onClick={() => setActive((i) => (i - 1 + photos.length) % photos.length)}
                  className="group absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#2C3830] shadow-md backdrop-blur transition-all hover:scale-110 hover:bg-white"
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="h-5 w-5 transition-transform group-hover:-translate-x-0.5" />
                </button>
                <button
                  data-testid="gallery-next"
                  onClick={() => setActive((i) => (i + 1) % photos.length)}
                  className="group absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#2C3830] shadow-md backdrop-blur transition-all hover:scale-110 hover:bg-white"
                  aria-label="Next photo"
                >
                  <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </button>
                <span
                  data-testid="gallery-counter"
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur"
                >
                  {active + 1} / {photos.length}
                </span>
              </>
            )}
          </div>

          <div className="mt-3 flex gap-3 overflow-x-auto rento-scroll-hide pb-1">
            {photos.map((p, i) => (
              <button
                key={i}
                data-testid={`thumb-${i}`}
                onClick={() => {
                  setActive(i);
                  setShowVideo(false);
                }}
                className={`h-20 w-24 shrink-0 overflow-hidden rounded-xl border-2 transition-all ${
                  active === i && !showVideo ? "border-[#D4A373]" : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
            {listing.video && (
              <button
                data-testid="video-thumb"
                onClick={() => setShowVideo(true)}
                className={`relative flex h-20 w-24 shrink-0 items-center justify-center rounded-xl border-2 bg-[#2C3830] text-white ${
                  showVideo ? "border-[#D4A373]" : "border-transparent"
                }`}
              >
                <PlayCircle className="h-7 w-7" />
              </button>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <span className="label-caps text-[#D4A373]">{typeLabel(listing.property_type)}</span>
          <h1 className="mt-2 font-display text-3xl font-medium leading-tight tracking-tight text-[#1A1A1A]">
            {listing.title}
          </h1>
          <div className="mt-3 flex items-center gap-1.5 text-[#6B7280]">
            <MapPin className="h-4 w-4 text-[#D4A373]" />
            {listing.estate ? `${listing.estate}, ` : ""}
            {listing.town}, {listing.county}
          </div>

          <div className="mt-5 flex items-end gap-1">
            <span data-testid="listing-price" className="font-display text-4xl font-semibold text-[#2C3830]">
              {money(listing.price, listing.currency)}
            </span>
            {listing.listing_type === "rent" && <span className="pb-1 text-[#6B7280]">/month</span>}
            {listing.listing_type === "short_term" && <span className="pb-1 text-[#6B7280]">/night</span>}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            {listing.size && (
              <div className="flex items-center gap-2 rounded-xl bg-white p-3 text-sm">
                <Maximize className="h-4 w-4 text-[#D4A373]" />
                <span className="text-[#6B7280]">Size</span>
                <span className="ml-auto font-medium text-[#2C3830]">{listing.size}</span>
              </div>
            )}
            {listing.is_storey && listing.floor_number != null && (
              <div className="flex items-center gap-2 rounded-xl bg-white p-3 text-sm">
                <Layers className="h-4 w-4 text-[#D4A373]" />
                <span className="text-[#6B7280]">Floor</span>
                <span className="ml-auto font-medium text-[#2C3830]">{listing.floor_number}</span>
              </div>
            )}
            <div className="flex items-center gap-2 rounded-xl bg-white p-3 text-sm">
              <Building2 className="h-4 w-4 text-[#D4A373]" />
              <span className="text-[#6B7280]">Type</span>
              <span className="ml-auto font-medium capitalize text-[#2C3830]">{listing.listing_type === "short_term" ? "Short stay" : listing.listing_type}</span>
            </div>
            {isSharedType(listing.property_type) && listing.gender_pref && listing.gender_pref !== "any" && (
              <div data-testid="listing-gender-pref" className="flex items-center gap-2 rounded-xl bg-white p-3 text-sm">
                <User className="h-4 w-4 text-[#D4A373]" />
                <span className="text-[#6B7280]">Roommate</span>
                <span className="ml-auto font-medium text-[#2C3830]">{genderPrefLabel(listing.gender_pref)}</span>
              </div>
            )}
          </div>

          {listing.listing_type === "short_term" && (
            <div data-testid="short-term-info" className="mt-6 rounded-2xl border border-border/60 bg-white p-5">
              <p className="font-display text-lg font-medium text-[#2C3830]">Stay details</p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                {listing.check_in_time && (
                  <div className="flex items-center gap-2 rounded-xl bg-[#F7F5F0] p-3">
                    <Clock className="h-4 w-4 text-[#D4A373]" />
                    <span className="text-[#6B7280]">Check-in</span>
                    <span className="ml-auto font-medium text-[#2C3830]">{listing.check_in_time}</span>
                  </div>
                )}
                {listing.check_out_time && (
                  <div className="flex items-center gap-2 rounded-xl bg-[#F7F5F0] p-3">
                    <Clock className="h-4 w-4 text-[#D4A373]" />
                    <span className="text-[#6B7280]">Check-out</span>
                    <span className="ml-auto font-medium text-[#2C3830]">{listing.check_out_time}</span>
                  </div>
                )}
                {(listing.min_nights || listing.max_nights) && (
                  <div className="col-span-2 flex items-center gap-2 rounded-xl bg-[#F7F5F0] p-3">
                    <CalendarRange className="h-4 w-4 text-[#D4A373]" />
                    <span className="text-[#6B7280]">Stay length</span>
                    <span className="ml-auto font-medium text-[#2C3830]">
                      {listing.min_nights || 1}–{listing.max_nights || 30} nights
                    </span>
                  </div>
                )}
              </div>
              {listing.st_addons?.length > 0 && (
                <div className="mt-4">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-[#2C3830]"><Sparkles className="h-4 w-4 text-[#D4A373]" /> Amenities</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {listing.st_addons.map((a) => (
                      <span key={a} data-testid={`addon-${a}`} className="rounded-full bg-[#F7F5F0] px-3 py-1 text-xs text-[#2C3830]">{a}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {listing.listing_type === "short_term" && (
            <div data-testid="availability-section" className="mt-6 rounded-2xl border border-border/60 bg-white p-5">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-[#D4A373]" />
                <h3 className="font-display text-lg font-medium text-[#2C3830]">Availability</h3>
              </div>
              {isOwner ? (
                <>
                  <p className="mt-2 text-sm text-[#6B7280]">Tap dates to block or unblock them for guests, then save.</p>
                  <Calendar mode="multiple" selected={blocked} onSelect={(d) => setBlocked(d || [])} disabled={{ before: new Date() }} className="mt-2" />
                  <Button data-testid="save-availability-btn" onClick={saveAvailability} disabled={savingAvail} className="mt-2 rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]">
                    {savingAvail ? "Saving…" : "Save availability"}
                  </Button>
                </>
              ) : (
                <>
                  <p className="mt-2 text-sm text-[#6B7280]">Struck-through nights are already booked or blocked by the host.</p>
                  <Calendar
                    mode="default"
                    modifiers={{ blocked }}
                    modifiersClassNames={{ blocked: "line-through text-destructive opacity-60" }}
                    disabled={[{ before: new Date() }, ...blocked]}
                    className="mt-2"
                  />
                </>
              )}
            </div>
          )}

          {/* Contact card / paywall */}
          <div className="relative mt-6 overflow-hidden rounded-2xl border border-border/60 bg-white p-5">
            <h3 className="font-display text-lg font-medium text-[#2C3830]">Owner / Agent contact</h3>
            {contact ? (
              <div data-testid="contact-revealed" className="mt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                  <User className="h-4 w-4 text-[#D4A373]" /> {contact.name}
                </div>
                <a
                  href={`tel:${contact.phone}`}
                  data-testid="contact-phone"
                  className="flex items-center gap-2 text-sm font-medium text-[#2C3830]"
                >
                  <Phone className="h-4 w-4 text-[#D4A373]" /> {contact.phone}
                </a>
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-[#2C3830]">
                    <Mail className="h-4 w-4 text-[#D4A373]" /> {contact.email}
                  </a>
                )}
              </div>
            ) : (
              <>
                <div className="mt-4 space-y-3 blur-sm select-none" aria-hidden>
                  <div className="flex items-center gap-2 text-sm"><User className="h-4 w-4" /> Jane Doe Properties</div>
                  <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4" /> 07XX XXX XXX</div>
                  <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4" /> hidden@email.com</div>
                </div>
                <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 bg-white/70 p-5 backdrop-blur-xl">
                  <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                    <Lock className="h-4 w-4" /> Unlock for {money(listing.unlock_fee, listing.currency)}
                  </div>
                  <Button
                    data-testid="unlock-contact-btn"
                    onClick={() => setPaywallOpen(true)}
                    className="w-full rounded-full bg-[#25D366] py-5 font-semibold text-white hover:bg-[#1fb85a]"
                  >
                    Pay with M-Pesa to reveal contact
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Map hint */}
          <div className="mt-6 rounded-2xl border border-border/60 bg-white p-5">
            <div className="flex items-center gap-2">
              <Map className="h-4 w-4 text-[#D4A373]" />
              <h3 className="font-display text-lg font-medium text-[#2C3830]">General area</h3>
            </div>
            <p data-testid="area-hint" className="mt-2 text-sm leading-relaxed text-[#6B7280]">
              {listing.area_map_hint || `${listing.town}, ${listing.county} — exact location shared after contact unlock.`}
            </p>
            <div className="mt-3">
              <MapView lat={listing.lat} lng={listing.lng} county={listing.county} label={`${listing.town}, ${listing.county}`} />
            </div>
          </div>
        </div>
      </div>

      {listing.description && (
        <div className="mt-10 max-w-3xl">
          <h2 className="font-display text-2xl font-medium text-[#2C3830]">About this property</h2>
          <p className="mt-3 whitespace-pre-line leading-relaxed text-[#6B7280]">{listing.description}</p>
        </div>
      )}

      {listing.floor_plan && (
        <div data-testid="floor-plan-section" className="mt-10 max-w-3xl">
          <h2 className="font-display text-2xl font-medium text-[#2C3830]">Floor plan</h2>
          <img data-testid="floor-plan-img" src={fileUrl(listing.floor_plan)} alt="Floor plan" className="mt-3 w-full rounded-2xl border border-border/60 bg-white object-contain" />
        </div>
      )}

      {/* Guest reviews */}
      <div data-testid="reviews-section" className="mt-10 max-w-3xl">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-medium text-[#2C3830]">Guest reviews</h2>
          {listing.rating_count > 0 && (
            <span className="flex items-center gap-1 text-sm text-[#6B7280]">
              <Star className="h-4 w-4 fill-[#D4A373] text-[#D4A373]" />
              <span data-testid="rating-avg" className="font-semibold text-[#2C3830]">{listing.rating_avg}</span>
              ({listing.rating_count})
            </span>
          )}
        </div>

        <div className="mt-4 rounded-2xl border border-border/60 bg-white p-5">
          <p className="text-sm font-medium text-[#2C3830]">Leave a review</p>
          <div className="mt-2 flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`star-${n}`} onClick={() => setRating(n)} aria-label={`${n} star`}>
                <Star className={`h-6 w-6 transition-colors ${n <= rating ? "fill-[#D4A373] text-[#D4A373]" : "text-[#D1D5DB]"}`} />
              </button>
            ))}
          </div>
          <Input data-testid="review-name" value={rvName} onChange={(e) => setRvName(e.target.value)} placeholder="Your name (optional)" className="mt-3 rounded-xl" />
          <Textarea data-testid="review-comment" value={rvComment} onChange={(e) => setRvComment(e.target.value)} placeholder="Share your experience…" className="mt-2 rounded-xl" />
          <Button data-testid="submit-review-btn" onClick={submitReview} disabled={submitting} className="mt-3 rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]">
            {submitting ? "Submitting…" : "Submit review"}
          </Button>
        </div>

        <div className="mt-4 space-y-3">
          {reviews.length === 0 ? (
            <p data-testid="reviews-empty" className="text-sm text-[#6B7280]">No reviews yet — be the first to share your experience.</p>
          ) : (
            reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl border border-border/60 bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#2C3830]">{r.guest_name}</span>
                  <span className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-[#D4A373] text-[#D4A373]" : "text-[#D1D5DB]"}`} />
                    ))}
                  </span>
                </div>
                {r.comment && <p className="mt-2 text-sm leading-relaxed text-[#6B7280]">{r.comment}</p>}
              </div>
            ))
          )}
        </div>
      </div>

      <PaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        listing={listing}
        onUnlocked={(c) => setContact(c)}
      />
    </div>
  );
}
